import React from 'react';
import { Buildings, Envelope, User } from '@phosphor-icons/react';

interface EmployeeItem {
    employee_name?: string;
    employeeName?: string;
    employee_email?: string;
    employeeEmail?: string;
    department_name?: string;
    departmentName?: string;
    job_title?: string;
    jobTitle?: string;
}

interface EmployeeCardProps {
    data: EmployeeItem[] | { data: EmployeeItem[]; metadata?: any; meta?: any; schema?: any };
    options?: {
        showAvatar?: boolean;
        showDepartment?: boolean;
        showManager?: boolean;
        maxCards?: number;
    };
}

const getInitials = (name: string): string => {
    if (!name) return '?';
    return name
        .split(' ')
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
};

const getAvatarColor = (name: string): string => {
    const colors = [
        'bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-purple-500',
        'bg-pink-500', 'bg-rose-500', 'bg-teal-500', 'bg-cyan-500',
        'bg-emerald-500', 'bg-amber-500',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

export const EmployeeCard: React.FC<EmployeeCardProps> = ({ data, options = {} }) => {
    const {
        showAvatar = true,
        showDepartment = true,
        maxCards = 10,
    } = options;

    let items: EmployeeItem[] = [];
    if (Array.isArray(data)) {
        items = data;
    } else if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        items = data.data;
    }

    const displayItems = items.slice(0, maxCards);

    if (!displayItems || displayItems.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <User size={28} className="text-gray-400" />
                </div>
                <span className="text-sm font-medium">No employees found</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2.5 p-3 h-full overflow-auto">
            {displayItems.map((item, index) => {
                const name = item.employeeName || item.employee_name || 'Unknown';
                const email = item.employeeEmail || item.employee_email || '';
                const department = item.departmentName || item.department_name || '';
                const jobTitle = item.jobTitle || item.job_title || '';

                return (
                    <div
                        key={`employee-${index}`}
                        className="group bg-white rounded-lg border border-gray-200 p-3.5 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
                    >
                        <div className="flex items-center gap-3">
                            {/* Avatar */}
                            {showAvatar && (
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 ${getAvatarColor(name)}`}>
                                    {getInitials(name)}
                                </div>
                            )}

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                                    {name}
                                </h3>
                                {jobTitle && (
                                    <p className="text-xs text-gray-500 truncate">{jobTitle}</p>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        {(department || email) && (
                            <div className="flex items-center justify-between text-xs text-gray-500 pt-2.5 mt-2.5 border-t border-gray-100">
                                {showDepartment && department ? (
                                    <div className="flex items-center gap-1.5 min-w-0">
                                        <Buildings size={14} className="text-gray-400 flex-shrink-0" />
                                        <span className="truncate">{department}</span>
                                    </div>
                                ) : (
                                    <div />
                                )}
                                {email && (
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                        <Envelope size={14} className="text-gray-400" />
                                        <span className="truncate max-w-[180px]">{email}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
